// Original fictional cases. Each linked lesson supplies the supporting concepts and sources.
export const marketCheckups = [
  {
    id: 'company-execution',
    label: 'Company check',
    title: '一家公司，四種不同的數字',
    description: '把獲利、現金、委託與拆股放在一起，檢查每個數字的口徑。',
    background: [
      '虛構公司 K 的全年歸屬普通股股東淨利為 1,200 萬元，營業現金流為 900 萬元，現金資本支出為 700 萬元。公司在年初以既有現金回購並註銷股份；假設回購不影響本年淨利，且本年沒有其他股數變動。',
      '先閱讀本年的財務與委託資料，再獨立演練未來「每 1 股拆成 2 股」的價格比較。拆股情境不納入前面的 EPS 計算。全部數字均為假設，忽略交易稅費；報價題另假設委託簿不變且沒有其他訂單競爭。',
    ],
    facts: [
      { label: '本年財務', value: '淨利 1,200 萬；營業現金流 900 萬；現金資本支出 700 萬元' },
      { label: '年初回購', value: '原流通 400 萬股；以每股 25 元回購並註銷 40 萬股' },
      { label: '賣方委託簿', value: '100 股 × 31.8 元；200 股 × 32 元；最後成交價 31.6 元' },
      { label: '獨立拆股情境', value: '每 1 股拆成 2 股；拆股前某日 32 元，拆股後某日 17 元；期間無配息' },
    ],
    questions: [
      {
        id: 'check-company-profit-cash',
        lessonId: 'free-cash-flow',
        question: '依本案例「營業現金流減現金資本支出」的定義，本年自由現金流是多少？',
        options: [
          '500 萬元，使用淨利扣掉資本支出',
          '200 萬元，使用營業現金流扣掉資本支出',
          '900 萬元，營業現金流已經扣完所有投資支出',
        ],
        answer: 1,
        explanation: '本題指定的 FCF＝900−700＝200 萬元；淨利 1,200 萬元屬於另一種會計衡量，不能直接代入這個公式。200 萬元也不是期末現金餘額，尚未反映回購、其他投資或融資活動，更不代表全數可自由分配。比較公司時要先核對 FCF 的定義。',
      },
      {
        id: 'check-company-buyback-denominator',
        lessonId: 'share-buybacks',
        question: '只看回購當年，尚未發生拆股；年初註銷後全年股數不變。哪組基本 EPS 與回購現金支出正確？',
        options: [
          'EPS 約 0.56 元；現金支出 1,000 萬元，先從淨利扣除回購金額',
          'EPS 為 3 元；現金支出 1,000 萬元，分母仍使用回購前股數',
          'EPS 約 3.33 元；現金支出 1,000 萬元，淨利仍為 1,200 萬元',
        ],
        answer: 2,
        explanation: '全年加權平均流通股數為 400−40＝360 萬股，所以基本 EPS＝1,200÷360≈3.33 元；買回股份支付 40 萬×25＝1,000 萬元現金。回購款不是本題的淨利費用，EPS 提高也不代表多賺了現金。題目假設獲利不變，實務仍須考慮資金成本與其他影響。',
      },
      {
        id: 'check-company-limit-depth',
        lessonId: 'market-limit-orders',
        question: '你送出買進 250 股、每股限價 31.9 元的委託。依固定委託簿，按照價格優先撮合，在這一次撮合中會發生什麼？',
        options: [
          '100 股以 31.8 元成交；其餘 150 股在這次撮合中無法成交',
          '250 股全部以限價 31.9 元成交',
          '100 股以 31.8 元、150 股以 32 元成交，平均為 31.92 元',
        ],
        answer: 0,
        explanation: '31.8 元符合買進限價，而且只有 100 股；32 元高於 31.9 元，不能用這張限價單買進。限價限制每一筆可接受的成交價格，不會創造 31.9 元的賣方，也不會保證買齊。剩餘委託會保留或取消，取決於另外設定的有效條件，本題沒有指定。',
      },
      {
        id: 'check-company-split-price-history',
        lessonId: 'stock-splits',
        question: '在獨立的拆股情境中，一直持有股份且沒有買賣、配息或費用。從拆股前某日的 32 元到拆股後某日的 17 元，如何正確比較這兩個觀察日的持股價值變化？',
        options: [
          '約 −46.88%，直接以 17÷32−1 計算',
          '約 ＋6.25%，先把拆股前價格換成每股 16 元再比較',
          '約 ＋106.25%，把拆股新增股數也另外當成一次報酬',
        ],
        answer: 1,
        explanation: '每 1 股拆成 2 股，原本 1 股×32 元對應後來 2 股×17 元，持股價值從 32 元變成 34 元，報酬為 34÷32−1＝6.25%。也可把拆股前價格調整為 16 元，再算 17÷16−1。拆股本身不創造這 6.25%；它來自兩個觀察日之間、扣除拆股比例後的市場價格變化。',
      },
    ],
  },
  {
    id: 'news-risk',
    label: 'News and risk',
    title: '通膨新聞，不能直接變成風險結論',
    description: '從物價和購買力出發，再檢查組合波動與尾端損失的數字。',
    background: [
      '某虛構經濟體連續三年同一月份的 CPI 指數為 100、108、110.16，三者使用相同基期。最後這一年，一筆假想資金持有滿一年，實際名目總報酬為 3.6%；一年前對同期通膨的預測是 2.8%。',
      '另有一份假想組合的歷史風險報告：A、B 各占 50%，同幣別、同觀察期間的年報酬標準差為 10% 與 18%，相關係數為 0。報告另列出一天、99% 信賴水準的 VaR 估計；年波動與一天損失門檻用途不同，不能直接互換。',
    ],
    facts: [
      { label: '同月份 CPI', value: '第 0 年 100 → 第 1 年 108 → 第 2 年 110.16' },
      { label: '最後一年資金報酬', value: '實際名目報酬 3.6%；事前預測通膨 2.8%；忽略稅費' },
      { label: '組合歷史估計', value: 'A、B 各 50%；年標準差 10%、18%；相關係數 0' },
      { label: '尾端風險報告', value: '一天、99% 信賴水準的 VaR：24,000 元，依報告模型估計' },
    ],
    questions: [
      {
        id: 'check-news-cpi-base',
        lessonId: 'cpi-inflation',
        question: '新聞說「通膨放慢」。依同基期、同月份的 CPI，比較第 2 年與第 1 年，哪個數字正確？',
        options: [
          '年增率 2.16%，直接把 110.16−108 當成百分比',
          '年增率 10.16%，以第 0 年的 100 作為當年比較基準',
          '年增率 2%；漲幅由前一年的 8% 放慢，但價格指數仍上升',
        ],
        answer: 2,
        explanation: '最後一年年增率＝110.16÷108−1＝2%；前一年為 108÷100−1＝8%。2.16 是指數點數差，10.16% 則是兩年的累積漲幅，都不是最後一年的年增率。漲幅下降不等於價格下降，且整體 CPI 也不等於每個家庭的個別物價變化。',
      },
      {
        id: 'check-news-realized-purchasing-power',
        lessonId: 'real-interest-rates',
        question: '使用最後一年已實現的 CPI 變化，並按精確公式換算，這筆資金的實際購買力報酬約是多少？',
        options: [
          '1.57%，使用 1.036÷1.02−1',
          '0.78%，使用一年前的通膨預測計算 1.036÷1.028−1',
          '5.67%，把報酬與通膨相乘：1.036×1.02−1',
        ],
        answer: 0,
        explanation: '已實現的同期通膨為 2%，所以實際購買力報酬＝1.036÷1.02−1≈1.57%。使用預測的 2.8% 會得到約 0.78%，這是另一種事前情境估計，不能代替事後結果。3.6%−2%＝1.6% 是近似算法；評估時也要對齊通膨和持有期間。',
      },
      {
        id: 'check-news-correlation-volatility',
        lessonId: 'correlation-risk',
        question: '用報告給定的 A、B 歷史估計值，以及各 50% 的固定權重，依兩資產公式計算組合年報酬標準差，約為多少？',
        options: [
          '14.00%，直接平均兩個標準差',
          '14.56%，用 50% 加權兩個變異數後開根號',
          '10.30%，將權重平方後計算變異數，共變動項為 0',
        ],
        answer: 2,
        explanation: '組合標準差＝√［0.5²×0.10²＋0.5²×0.18²＋2×0.5×0.5×0.10×0.18×0］≈10.30%。相關係數為 0 不會消除兩項資產自身的波動，也不代表不會同時下跌。這是依指定資料計算的歷史估計，未來波動與相關性都可能改變。',
      },
      {
        id: 'check-news-var-tail-meaning',
        lessonId: 'tail-risk',
        question: '這份報告的「一天、99% 信賴水準 VaR 為 24,000 元」，在其模型假設下應如何解讀？',
        options: [
          '未來 100 個交易日中，損失最嚴重那一天的平均損失是 24,000 元',
          '它是一天損失分布的分位門檻；仍需另外了解超過 24,000 元後的損失程度',
          '它是損失最嚴重的 1% 情境之平均損失，已經描述尾端平均幅度',
        ],
        answer: 1,
        explanation: 'VaR 指定期間與信賴水準下的損失分位門檻，不是未來一段期間最差日的預期損失，也不是最差尾端的平均損失。後者較接近 Expected Shortfall 所要衡量的概念。越過門檻的損失可能更大，模型本身也可能失準，不能把 24,000 元視為一天損失上限。',
      },
    ],
  },
];
